import { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import CxfMonacoEditor from "components/CxfMonacoEditor";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormLabel from "@mui/material/FormLabel";
import SoftSelect from "components/SoftSelect";
import SoftTypography from "components/SoftTypography";
import { getFlowSession, getFlowSessions } from "api/FlowDesignerApis";
import { THEME_COLORS, THEME_TYPES } from "constants/theme";
import ToolboxDetailHeader from "../ToolboxDetailHeader";

const SessionViewer = () => {
   const { user } = useAuth0();
   const isDark = useSelector((store) => store?.portal?.settings.themeMode) === THEME_TYPES.DARK;
   const [sessionIds, setSessionIds] = useState([]);
   const [selectedSessionId, setSelectedSessionId] = useState("");
   const [session, setSession] = useState(null);
   const [loadingSessions, setLoadingSessions] = useState(true);
   const [loadingSession, setLoadingSession] = useState(false);
   const [error, setError] = useState("");
   const sessionRequestId = useRef(0);

   const handleSessionChange = async (option) => {
      const sessionId = option?.value || "";
      const requestId = ++sessionRequestId.current;
      setSelectedSessionId(sessionId);
      setSession(null);
      setError("");

      if (!sessionId) {
         setLoadingSession(false);
         return;
      }

      setLoadingSession(true);
      const response = await getFlowSession(user.stripeId, sessionId);

      if (requestId !== sessionRequestId.current) return;

      if (response?.isSuccessful) {
         setSession(response.resultObject);
      } else {
         setError(response?.message || "Unable to load the selected session.");
      }
      setLoadingSession(false);
   };

   useEffect(() => {
      let active = true;

      const loadSessions = async () => {
         if (!user?.stripeId) {
            setLoadingSessions(false);
            return;
         }

         setLoadingSessions(true);
         setError("");
         const response = await getFlowSessions(user.stripeId);

         if (!active) return;

         if (response?.isSuccessful) {
            setSessionIds(Array.isArray(response.resultObject) ? response.resultObject : []);
         } else {
            setError(response?.message || "Unable to load sessions.");
         }
         setLoadingSessions(false);
      };

      loadSessions();
      return () => {
         active = false;
      };
   }, [user?.stripeId]);

   return (
      <Box sx={{ height: "100%", minHeight: 0, p: "20px 18px", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
         <ToolboxDetailHeader title="Session Viewer" />
         <SoftTypography variant="body2" color="text" sx={{ mt: 1, mb: 3, fontSize: "14px" }}>
            Select a session to view its data.
         </SoftTypography>

         {loadingSessions ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
               <CircularProgress size={28} />
            </Box>
         ) : (
            <>
               {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
               {sessionIds.length === 0 ? (
                  <Box sx={{ border: "1px solid var(--designer-shell-border)", borderRadius: "8px", p: 2 }}>
                     <SoftTypography variant="body2" color="text">No sessions were found for this tenant.</SoftTypography>
                  </Box>
               ) : (
                  <Box sx={{ width: "100%" }}>
                     <FormLabel sx={{ fontSize: "12px", pl: "12px", mb: "6px", display: "block" }}>
                        Session
                     </FormLabel>
                     <SoftSelect
                        name="session"
                        placeholder="Search or select a session"
                        useDefaultValueContainer
                        value={selectedSessionId ? { label: selectedSessionId, value: selectedSessionId } : null}
                        options={sessionIds.map((sessionId) => ({ label: sessionId, value: sessionId }))}
                        onChange={handleSessionChange}
                        isSearchable
                        isClearable
                        openMenuOnFocus
                        closeMenuOnSelect
                        noOptionsMessage={() => "No matching sessions"}
                        menuPlacement="bottom"
                        menuPosition="fixed"
                     />
                  </Box>
               )}

               <Box sx={{ flex: 1, minHeight: 0, mt: 3, display: "flex", flexDirection: "column" }}>
                  {loadingSession ? (
                     <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <CircularProgress size={28} />
                     </Box>
                  ) : selectedSessionId && session !== null ? (
                     <Box
                        aria-label={`Session ${selectedSessionId} data`}
                        sx={{
                           flex: 1,
                           minHeight: 0,
                           overflow: "hidden",
                           border: "1px solid var(--designer-shell-border)",
                           borderRadius: "8px",
                           backgroundColor: isDark ? THEME_COLORS.DARK.PRIMARY_BG : THEME_COLORS.LIGHT.PRIMARY_BG
                        }}
                     >
                        <CxfMonacoEditor
                           language="json"
                           value={JSON.stringify(session, null, 2)}
                           height="100%"
                           width="100%"
                           options={{
                              readOnly: true,
                              domReadOnly: true,
                              minimap: { enabled: false },
                              wordWrap: "on",
                              folding: true,
                              scrollBeyondLastLine: false,
                              renderLineHighlight: "none",
                              automaticLayout: true,
                              padding: { top: 12 },
                              scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 }
                           }}
                        />
                     </Box>
                  ) : null}
               </Box>
            </>
         )}
      </Box>
   );
};

export default SessionViewer;
